// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// 🎯 EXERCISE SESSION COMPONENT
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { createExerciseSession } from '../../services/exerciseSessions';
import type { Exercise, ScoreData, ScoringType } from '../../types';
import { ImageLightbox } from './ImageLightbox';
import './ExerciseSession.css';

interface ExerciseSessionProps {
  exercise: Exercise;
  onComplete: () => void;
  onCancel: () => void;
}

export const ExerciseSession: React.FC<ExerciseSessionProps> = ({
  exercise,
  onComplete,
  onCancel,
}) => {
  const { player } = useAuth();
  const { t } = useTranslation();

  // Timer
  const [startTime] = useState(new Date());
  const [elapsedSeconds, setElapsedSeconds] = useState(0);

  // Score tracking
  const [scoreData, setScoreData] = useState<ScoreData>({
    attempts: [],
    best_score: 0,
    average_score: 0,
  });

  // Additional tracking fields
  const [totalScore, setTotalScore] = useState(0);
  const [successCount, setSuccessCount] = useState(0);
  const [failCount, setFailCount] = useState(0);

  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);

  // Lightbox state
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  // Timer effect
  useEffect(() => {
    const interval = setInterval(() => {
      setElapsedSeconds(Math.floor((Date.now() - startTime.getTime()) / 1000));
    }, 1000);

    return () => clearInterval(interval);
  }, [startTime]);

  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
  };

  const handleScoreChange = (field: string, value: number) => {
    if (field === 'best_score') {
      setScoreData(prev => ({ ...prev, best_score: value }));
    } else if (field === 'average_score') {
      setScoreData(prev => ({ ...prev, average_score: value }));
    }
  };

  const handleComplete = async () => {
    if (!player) return;

    setSaving(true);
    try {
      const scoringType: ScoringType = exercise.scoring_type as ScoringType;
      
      // Build score data based on scoring type
      const finalScoreData: ScoreData = {
        ...scoreData,
        best_score: scoreData.best_score,
        average_score: totalScore,
      };

      // Add type-specific data
      if (scoringType === 'success_rate') {
        finalScoreData.success_count = successCount;
        finalScoreData.fail_count = failCount;
      }
      if (scoringType === 'points' || scoringType === 'balls_potted' || scoringType === 'custom') {
        finalScoreData.total_score = totalScore;
      }

      const durationMinutes = Math.max(1, Math.floor(elapsedSeconds / 60)); // Mindestens 1 Minute

      await createExerciseSession({
        exercise_id: exercise.id,
        player_id: player.id,
        score_data: finalScoreData,
        score: totalScore || scoreData.best_score || 0,
        duration_minutes: durationMinutes,
        notes: notes.trim() || undefined,
      });

      onComplete();
    } catch (err) {
      console.error('Failed to save session:', err);
      alert(t('training.sessions.saveFailed'));
    } finally {
      setSaving(false);
    }
  };

  const renderScoreInput = () => {
    const scoringType: ScoringType = exercise.scoring_type as ScoringType;

    switch (scoringType) {
      case 'points':
        return (
          <div className="score-input">
            <label>{t('training.sessions.points')}</label>
            <input
              type="number"
              value={totalScore || 0}
              onChange={(e) => setTotalScore(Number(e.target.value))}
              min="0"
            />
          </div>
        );

      case 'balls_potted':
        return (
          <div className="score-input">
            <label>{t('training.sessions.ballsPotted')}</label>
            <input
              type="number"
              value={totalScore || 0}
              onChange={(e) => setTotalScore(Number(e.target.value))}
              min="0"
            />
          </div>
        );

      case 'success_rate':
        return (
          <>
            <div className="score-input">
              <label>{t('training.sessions.successCount')}</label>
              <input
                type="number"
                value={successCount || 0}
                onChange={(e) => setSuccessCount(Number(e.target.value))}
                min="0"
              />
            </div>
            <div className="score-input">
              <label>{t('training.sessions.failCount')}</label>
              <input
                type="number"
                value={failCount || 0}
                onChange={(e) => setFailCount(Number(e.target.value))}
                min="0"
              />
            </div>
            {(successCount || 0) + (failCount || 0) > 0 && (
              <div className="success-rate-display">
                {t('training.sessions.successRate')}: {' '}
                {Math.round(
                  ((successCount || 0) / 
                  ((successCount || 0) + (failCount || 0))) * 100
                )}%
              </div>
            )}
          </>
        );

      case 'time':
        return (
          <div className="score-input">
            <label>{t('training.sessions.bestTime')}</label>
            <input
              type="number"
              value={scoreData.best_score || 0}
              onChange={(e) => handleScoreChange('best_score', Number(e.target.value))}
              min="0"
              placeholder={t('training.sessions.seconds')}
            />
            <small>{t('training.sessions.seconds')}</small>
          </div>
        );

      case 'attempts':
        return (
          <>
            <div className="score-input">
              <label>{t('training.sessions.bestScore')}</label>
              <input
                type="number"
                value={scoreData.best_score || 0}
                onChange={(e) => handleScoreChange('best_score', Number(e.target.value))}
                min="0"
              />
            </div>
          </>
        );

      case 'custom':
      default:
        return (
          <div className="score-input">
            <label>{t('training.sessions.score')}</label>
            <input
              type="number"
              value={totalScore || 0}
              onChange={(e) => setTotalScore(Number(e.target.value))}
              min="0"
            />
          </div>
        );
    }
  };

  return (
    <div className="exercise-session">
      <div className="session-header">
        <h2>🎯 {exercise.title}</h2>
        <div className="session-timer">
          ⏱️ {formatTime(elapsedSeconds)}
        </div>
      </div>

      {exercise.instructions && (
        <div className="session-instructions">
          <h3>{t('training.exercises.instructions')}</h3>
          <p>{exercise.instructions}</p>
        </div>
      )}

      {exercise.images && exercise.images.length > 0 && (
        <div className="session-images">
          <div className="image-thumbnails">
            {exercise.images.map((img, idx) => (
              <div
                key={idx}
                className="image-thumbnail"
                onClick={() => {
                  setLightboxIndex(idx);
                  setLightboxOpen(true);
                }}
              >
                <img src={img} alt={`Exercise ${idx + 1}`} />
                <div className="thumbnail-overlay">
                  <span>🔍</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="session-scoring">
        <h3>{t('training.sessions.recordScore')}</h3>
        <div className="score-inputs">
          {renderScoreInput()}
        </div>
      </div>

      <div className="session-notes">
        <label>{t('training.sessions.notes')}</label>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          placeholder={t('training.sessions.notesPlaceholder')}
          rows={4}
        />
      </div>

      <div className="session-actions">
        <button onClick={onCancel} disabled={saving} className="btn-secondary">
          {t('common.cancel')}
        </button>
        <button onClick={handleComplete} disabled={saving} className="btn-primary">
          {saving ? t('common.saving') : t('training.sessions.completeSession')}
        </button>
      </div>

      {/* Image Lightbox */}
      {exercise.images && exercise.images.length > 0 && (
        <ImageLightbox
          images={exercise.images}
          initialIndex={lightboxIndex}
          isOpen={lightboxOpen}
          onClose={() => setLightboxOpen(false)}
        />
      )}
    </div>
  );
};

